'use client';

import ActivityProductGrid from '@/app/(activity)/components/ActivityProductGrid';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useAppCommunication } from '@/providers/app-communication-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import { useContext, useEffect } from 'react';
import { Banner, PhotoWall, ProductShowcase } from '.';
import { usePinkLeopardTracking } from '../hooks/usePinkLeopardTracking';
import ColorLists from './ColorLists';
import ViewMoreButton from './ViewMoreButton';

interface PinkLeopardGlassesContentProps {
  pageData: {
    banner1: {
      image: string;
      title?: string; // PC端使用
      word?: string; // WAP端使用
      description?: string; // PC端使用
      aspectRatio?: string; // 宽高比
      jump_type?: number; // 跳转类型：1-商品列表，2-商品详情
      jump_value?: string; // 跳转值
    };
    product_lists_one: {
      title_word?: string;
      description?: string;
      btn_word?: string;
      lists: Array<{
        id: number;
        imageUrl?: string;
        title?: string;
        description?: string;
      }>;
    };
    product_lists_two: Array<{
      id: string;
      btn_word?: string;
      img_word?: string;
      chooseIndex?: number;
      color_arr?: Array<{
        colorImage?: string;
        bgColor?: string;
        bgColor1?: string;
        transparency?: string;
        transparency1?: string;
        isSolid?: boolean;
      }>;
      leg_img?: string;
      frame_img?: string;
      shop_sku?: string;
      business_sku?: string;
      sku?: string;
      short_name?: string;
      name?: string;
      price_info?: any;
    }> | null;
    swiper_part: {
      title?: string;
      number?: string;
      bg_img?: string;
      word_arr?: Array<{
        title: string;
        description: string;
      }>;
      swiper_lists: Array<{
        image: string;
        shop_sku?: string;
      }>;
    };
    list_part: {
      group: string;
      title?: string;
    };
  };
  productListsOne: any[];
  productListsTwo: any[];
  deviceType: EPlatform;
}

export default function PinkLeopardGlassesContent({
  pageData,
  productListsOne,
  productListsTwo,
  deviceType,
}: PinkLeopardGlassesContentProps) {
  const { deviceType: contextDeviceType } = useContext(ResponsiveContext);
  const actualDeviceType = contextDeviceType || deviceType; // 优先使用context中的deviceType
  // 使用deviceType判断是否在App内
  const isInApp = [EPlatform.ANDROID, EPlatform.IOS].includes(actualDeviceType);
  // 使用公共的App通信Provider获取用户信息准备状态
  const { isUserInfoReady } = useAppCommunication();

  const {
    trackPageView,
    trackViewMoreExposure,
    trackViewMoreClick,
    trackProductListPitsExposure,
    trackProductListPitsClick,
  } = usePinkLeopardTracking();

  // APP内时监听appView事件（埋点相关）
  useEffect(() => {
    if (isInApp) {
      window.addEventListener('appView', trackPageView);
      return () => {
        window.removeEventListener('appView', trackPageView);
      };
    }
  }, [isInApp, trackPageView]);

  // 页面加载完成后触发页面浏览埋点
  useEffect(() => {
    trackPageView();
  }, [trackPageView]);

  return (
    <Box className="w-full">
      {/* Banner区域 - 对应Vue版本的Banner */}
      {pageData.banner1 && <Banner bannerData={pageData.banner1} deviceType={deviceType} />}

      {/* PC端：只显示product_lists_one，product_lists_two用ColorLists */}
      {deviceType === EPlatform.WEB ? (
        <>
          {/* PC端商品列表区域 */}
          {Array.isArray(pageData.product_lists_one?.lists) && productListsOne.length > 0 && (
            <ProductShowcase
              showcaseData={{
                title_word: pageData.product_lists_one?.title_word,
                description: pageData.product_lists_one?.description,
                lists: productListsOne,
              }}
              productList={productListsOne}
              deviceType={deviceType}
              sdName="商品位1"
            />
          )}
        </>
      ) : (
        <>
          {/* WAP端：两个ProductLists，背景不同 */}

          {/* 商品列表区域一 - 背景 #F3F4F4，商品项背景白色 */}
          {Array.isArray(pageData.product_lists_one?.lists) && productListsOne.length > 0 && (
            <div className="lists-one bg-[#F3F4F4]">
              <ProductShowcase
                showcaseData={{
                  title_word: pageData.product_lists_one?.title_word,
                  description: pageData.product_lists_one?.description,
                  btn_word: pageData.product_lists_one?.btn_word,
                  lists: productListsOne,
                }}
                productList={productListsOne}
                deviceType={deviceType}
                sdName="商品位1"
                containerStyle="bg-white" // 商品项背景白色
              />
            </div>
          )}

          {/* 商品列表区域二 - 商品项背景 #f2f3f4，图片有对比度滤镜 */}
          {productListsTwo.length > 0 && (
            <div className="lists-two">
              <ProductShowcase
                showcaseData={{
                  title_word: (pageData.product_lists_two as any)?.title_word,
                  description: (pageData.product_lists_two as any)?.description,
                  btn_word: (pageData.product_lists_two as any)?.btn_word,
                  lists: productListsTwo as any, // 使用处理后的数据
                }}
                productList={productListsTwo}
                deviceType={deviceType}
                sdName="商品位2"
                containerStyle="bg-[#f2f3f4]" // 商品项背景灰色
                imageFilter="contrast-90" // 图片滤镜效果
              />
            </div>
          )}
        </>
      )}

      {/* 照片墙区域 - 对应Vue版本的PhotoWall */}
      {pageData.swiper_part && pageData.swiper_part.swiper_lists.length > 0 && (
        <PhotoWall swiperPart={pageData.swiper_part} deviceType={deviceType} />
      )}

      {/* PC端专用：ColorLists区域 - 对应Vue版本的ColorLists */}
      {deviceType === EPlatform.WEB &&
        Array.isArray(productListsTwo) &&
        productListsTwo.length > 0 && <ColorLists productListsTwo={productListsTwo} />}

      {/* 商品列表 - 对应Vue版本的GoodsLists */}
      {pageData.list_part && (
        <>
          {/* 用户信息准备完成后显示商品列表 */}
          {isUserInfoReady && (
            <div className={`${deviceType === EPlatform.WEB ? 'pb-[150px]' : 'pb-[55px]'}`}>
              <ActivityProductGrid
                title={pageData.list_part.title || ''}
                deviceType={deviceType}
                onProductClick={(product, index) => {
                  // 触发商品列表坑位点击埋点
                  trackProductListPitsClick(product, index, '商品列表位');
                }}
                onProductExp={(product, index) => {
                  // 触发商品列表坑位曝光埋点
                  trackProductListPitsExposure(product, index, '商品列表位');
                }}
                productCardProps={{
                  showCorrelation: true,
                }}
                showSort
                apiConfig={{
                  group_id: pageData.list_part.group,
                  limit: 20,
                }}
                padding={{
                  wap: 'px-[14px]',
                  pc:
                    deviceType === EPlatform.WEB
                      ? 'md:px-[100px] lg:px-[133px] xl:px-[167px] 2xl:px-[180px] 3xl:px-[200px]'
                      : 'px-[120px]',
                }}
              />

              {/* VIEW MORE按钮 - 与Vue版本完全一致 */}
              <ViewMoreButton
                groupId={pageData.list_part.group}
                deviceType={deviceType}
                onExposure={() => trackViewMoreExposure('商品列表位')}
                onClick={() => trackViewMoreClick('商品列表位')}
              />
            </div>
          )}
        </>
      )}
    </Box>
  );
}
